import type { FormFieldFieldHelper } from '@ingka/form-field';
import FormField from '@ingka/form-field';
import Select, { Option } from '@ingka/select';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shouldValidate } from '../../utils';
import { FormFieldChildren } from './FormFieldChildren';
import { createDateString, parseDateString, twoDigits } from './utils';
import styles from './ControlledYearMonthDropdown.module.scss';

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// For remapping the format error key to our own key. It does not make sense to display
// "Invalid format" in this component since since it is dropdowns and not text input.
const FORMAT_ERROR_KEY = 'form-error-message-date-format';
const REQUIRED_ERROR_KEY = 'lottery-form-error-message-year-month-required';
const translationKeyMap: Record<string, string> = {
  [FORMAT_ERROR_KEY]: REQUIRED_ERROR_KEY,
};

export type ControlledYearMonthFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = {
  labelKeys: {
    year: string;
    month: string;
  };
  placeholderKeys: {
    year: string;
    month: string;
  };
  control: Control<TFieldValues>;
  name: TName;
  /**
   * Day of month that will be added to the date-string
   */
  day: number | ((date: Date) => number);
  disabled?: boolean;
  fieldHelper?: FormFieldFieldHelper;
  /**
   * onChange will be called when valid date is selected (which is when both dropdowns have an
   * option selected)
   */
  onChange?: () => void;
};

/**
 * Render two dropdowns to select year and month
 *
 * The two inputs will write to the same form state field using the format 'YYYY-MM-DD'.
 */
export const ControlledYearMonthDropdown = <TFieldValues extends FieldValues>({
  control,
  labelKeys,
  placeholderKeys,
  name,
  disabled = false,
  fieldHelper,
  day,
  onChange: parentOnChange,
}: ControlledYearMonthFieldProps<TFieldValues, FieldPath<TFieldValues>>) => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const longMonthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' });
  const today = new Date().getFullYear();
  const years = [0, 1, 2, 3, 4].map((y) => (today + y).toString());

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
        const { year, month } = parseDateString(value);
        return (
          <FormField
            shouldValidate={shouldValidate(error)}
            valid={!error}
            validation={{
              id: `controlled-year-month-dropdown--${name}`,
              msg: error?.message ? `${t(translationKeyMap[error.message] ?? error.message)}` : '',
              type: 'error',
            }}
            fieldHelper={fieldHelper}
          >
            <FormFieldChildren
              render={(describedById) => (
                <div className={styles['wrapper']}>
                  <div className={styles['year']}>
                    <Select
                      id={`lottery-form-${name}-year`}
                      describedById={describedById}
                      label={`${t(labelKeys.year)}`}
                      onChange={(e) => {
                        const year = e.target.value.replace(/\D/g, '');
                        onChange(createDateString(year, month, day));
                        if (parentOnChange && year && month) {
                          parentOnChange();
                        }
                      }}
                      onBlur={onBlur}
                      value={year}
                      disabled={disabled}
                      hintText={`${t(placeholderKeys.year)}`}
                    >
                      {years.map((year) => (
                        <Option key={year} value={year} name={year} />
                      ))}
                    </Select>
                  </div>
                  <div className={styles['month']}>
                    <Select
                      id={`lottery-form-${name}-month`}
                      describedById={describedById}
                      label={`${t(labelKeys.month)}`}
                      onChange={(e) => {
                        const month = e.target.value.replace(/\D/g, '');
                        onChange(createDateString(year, month, day));
                        if (parentOnChange && year && month) {
                          parentOnChange();
                        }
                      }}
                      onBlur={onBlur}
                      value={month}
                      disabled={disabled}
                      hintText={`${t(placeholderKeys.month)}`}
                    >
                      {months.map((month) => (
                        <Option
                          key={month}
                          value={twoDigits(month)}
                          name={longMonthFormatter.format(new Date(2000, month - 1))}
                        />
                      ))}
                    </Select>
                  </div>
                </div>
              )}
            />
          </FormField>
        );
      }}
    />
  );
};
